import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { getReports, ReportListItem } from '../Api/Reports/reportsApi';
import { addError } from './Error';
import { getSelectedAccountIdsForUrl } from './User';

const subject = new BehaviorSubject<ReportListItem[]>([]);
const loadingSubject = new BehaviorSubject<boolean>(false);

export const getReportsSubject = (): BehaviorSubject<ReportListItem[]> => subject;
export const getReportsLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

export interface UseReportsReturn {
  reports: ReportListItem[];
  loading: boolean;
  fetchReports: (forceRefresh?: boolean) => Promise<void>;
  clearReports: () => void;
}

export const useReports = (): UseReportsReturn => {
  const [reports, setReports] = useState<ReportListItem[]>(subject.getValue());
  const [loading, setLoading] = useState<boolean>(loadingSubject.getValue());

  useEffect(() => {
    const subSub = subject.subscribe(setReports);
    const loadingSub = loadingSubject.subscribe(setLoading);
    return () => {
      subSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const fetchReports = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue().length > 0 && !forceRefresh) return;

    const accountIds = getSelectedAccountIdsForUrl();
    if (!accountIds.length) {
      subject.next([]);
      addError('reports', undefined);
      return;
    }

    loadingSubject.next(true);

    try {
      const { data, error } = await getReports(accountIds);
      if (data) {
        subject.next(data);
        addError('reports', undefined);
      }
      if (error) {
        subject.next([]);
        addError('reports', error.message);
      }
    } finally {
      loadingSubject.next(false);
    }
  };

  const clearReports = () => subject.next([]);

  return { reports, loading, fetchReports, clearReports };
};
